<template>
	<registration-container>
		<div class="container">
			<div class="title p-typography-bold-18">{{ getPersonalizedValue("title") }}</div>
			<div class="input-area">
				<marketing-properties-item
					v-for="select in renderedSelects"
					:key="select.index"
					:component="select.component"
					:question="select.question"
					@updated="select.updated"
				/>
			</div>
			<p-button
				type="cta"
				class="submit-button"
				:disabled="!submittable || informationSessionStatus === 'TRANSACTING'"
				@onClick="submit">
				<template v-if="informationSessionStatus !== 'TRANSACTING'">
					{{ getPersonalizedValue("submitButtonText") }}
				</template>
				<p-loader-dots v-else />
			</p-button>
		</div>
	</registration-container>
</template>
<script>
import RegistrationContainer from "vue/registration/common/page-structure/registration-container.vue";
import MarketingPropertiesItem from "vue/registration/subpages/marketing-properties-item.vue";
import PersonalizationEngine from "js/registration/personalized-experiences/personalizations.js";
export default {
	components: {
		RegistrationContainer,
		MarketingPropertiesItem
	},
	data() {
		// Return a blank questionTree object
		return {
			questionTree: []
		}
	},
	mounted() {
		// Configure the question tree based on the first available question
		for (let i = 0; i < this.getPersonalizedValue("configuration").start.length; i++) {
			this.questionTree.push({
				"key": this.getPersonalizedValue("configuration").start[i],
				"value": this.getPersonalizedValue("configuration").questions[this.getPersonalizedValue("configuration").start[i]].default,
				"children": []
			});
		}
	},
	computed: {
		/**
		 * Get the registration personalization configuration from the information session
		 */
		registrationPersonalization() {
			return this.informationSession.getProperty("registrationPersonalization", "registrationPersonalization");
		},
		/**
		 * Get the personalization type based on what is requested
		 */
		personalizationTypeArray() {
			if (this.registrationPersonalization && this.registrationPersonalization.startTemplate) {
				return ["template", this.registrationPersonalization.startTemplate,];
			}
			return ["default"];
		},
		/**
		 * Determine whether or not the button is enabled
		 */
		submittable() {
			/**
			 * Traverse through the tree recursively, checking that all of the expected questions are answered
			 * @param {Object} rootNode Tree node object attribute
			 * @return {Boolean} Are all available questions answered?
			 */
			function traverseTree(rootNode) {
				// Loop through all of the objects in our rootNode array
				for (let i = 0; i < rootNode.length; i++) {
					if (!rootNode[i].value) { // Check that the current node has a value
						return false;
					} else if (!traverseTree(rootNode[i].children)) { // Check that all children attributes have a value
						return false;
					}
				}
				return true; // If we get to this point, we have checked the entire tree from this point onwards
			}
			return traverseTree(this.questionTree);
		},
		/**
		 * Construct the dropdown items that need to be rendered to the object
		 */
		renderedSelects() {
			// Since we need to reference "this" inside a nested scope-changed function, define "self" to the value
			const self = this;
			let _return = [];
			/**
			 * Traverse through the tree recursively
			 * @param {Object} rootNode Tree node object attribute 
			 * @param {String} parentValue Value of the parent node for the item index value
			 * @param {Number} nestingLevel How far in deep are we?
			 */
			function traverseTree(rootNode, parentValue, nestingLevel) {
				// Loop through all of the objects in our rootNode array
				for (let i = 0; i < rootNode.length; i++) {
					// Construct a flattened node for rendering purposes
					let item = {
						index: `${rootNode[i].key}-${nestingLevel}-${parentValue}-${i}`, // VUE :key attribute
						question: self.getPersonalizedValue("configuration").questions[rootNode[i].key].question, // The title of the item
						component: self.getPersonalizedValue("configuration").questions[rootNode[i].key].component, // Available choices, to be generated below
						/**
						 * Select on change handler, poised to reconstruct the tree based on the next available questions
						 * @param {String} value New value
						 */
						updated(value, nextQuestionArray) {
							// Update the value
							rootNode[i].value = value;
							// Reconstruct the children elements
							let children = [];
							// Add all the children
							for (let j = 0; j < nextQuestionArray.length; j++) {
								children.push({
									"key": nextQuestionArray[j],
									"value": self.getPersonalizedValue("configuration").questions[nextQuestionArray[j]].default,
									"children": []
								});
							}
							// Reset our node children
							rootNode[i].children = children;
						}
					}
					_return.push(item);
					// Add the children questions to the flattened object
					traverseTree(rootNode[i].children, rootNode[i].value, nestingLevel + 1);
				}
			}
			traverseTree(self.questionTree, null, 0);
			return _return;
		}
	},
	props: {
		informationSession: {
			required: true,
		},
		informationSessionStatus: {
			required: true,
		},
	},
	methods: {
		getPersonalizedValue(string) {
			return PersonalizationEngine.getValue(this.personalizationTypeArray, ["marketingProperties", string]);
		},
		/**
		 * Submit
		 */
		submit() {
			// Construct the data object
			let self = this;
			let data = {};
			/**
			 * Traverse through the tree recursively, extracting the answer dictionary
			 * @param {Object} rootNode Tree node object attribute
			 */
			function traverseTree(rootNode) {
				// Loop through all of the objects in our rootNode array
				for (let i = 0; i < rootNode.length; i++) {
					// Extract the corresponding storage_key and link it to the node value
					data[self.getPersonalizedValue("configuration").questions[rootNode[i].key].storage_key] = rootNode[i].value;
					// Do the same for all the children
					traverseTree(rootNode[i].children);
				}
			}
			traverseTree(this.questionTree);
			// Send the event out to the backend system
			if (this.informationSession !== "TRANSACTING") {
				this.$emit("updatePropertyGroups", {
					marketingProperties: {
						fields: {
							marketingProperties: data
						}
					}
				});
			}
		}
	}
};
</script>
<style scoped lang="scss">
.container {
	padding: 18px 18px 30px 18px;
	.title {
		color: $p-color-grey-600;
		margin-bottom: 30px;
	}
	.input-area {
		min-height: 260px;
		margin-top: 18px;
	}
	.submit-button {
		width: 100%;
	}
}
</style>